/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Image } from '@fluentui/react/lib/Image';
import { Pivot, PivotItem } from '@fluentui/react/lib/Pivot';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useFormState } from 'src/core/hooks/useFormState';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { logButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getStylist } from 'src/core/themes/luisStylist';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { FormValidators } from 'src/core/utils/formValidators';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';

export type CreateCluEntityDto = { name: string };
type CreateCluEntityModalProps = BasicModalProps<CreateCluEntityDto> & { createLocally?: boolean };

const messages = defineMessages({
    list: { id: 'clu.cluEntityCreationModal.list', defaultMessage: 'List' },
    regex: { id: 'clu.cluEntityCreationModal.regex', defaultMessage: 'Regex' },
    learned: { id: 'clu.cluEntityCreationModal.learned', defaultMessage: 'Learned' },
    prebuilt: { id: 'clu.cluEntityCreationModal.prebuilt', defaultMessage: 'Prebuilt' },
    addEntity: { id: 'clu.cluEntityCreationModal.addEntity', defaultMessage: 'Add entity' },
    nameLabel: { id: 'clu.cluEntityCreationModal.nameLabel', defaultMessage: 'Entity name' },
    modalTitle: { id: 'clu.cluEntityCreationModal.modalTitle', defaultMessage: 'Add an entity' },
    nameSectionLabel: { id: 'clu.cluEntityCreationModal.nameSectionLabel', defaultMessage: 'Name an entity' },
    namePlaceholder: { id: 'clu.cluEntityCreationModal.namePlaceholder', defaultMessage: 'Type entity name ...' },
    examplesHeader: { id: 'clu.cluEntityCreationModal.examplesHeader', defaultMessage: 'For example, these utterances:' },
    utteranceExample2: { id: 'clu.cluEntityCreationModal.utteranceExample2', defaultMessage: '"Fly me to Rio on the 24th"' },
    addEntityFailed: { id: 'clu.cluEntityCreationModal.addEntityFailed', defaultMessage: 'Adding entity "{ entityName }" failed' },
    utteranceExample1: { id: 'clu.cluEntityCreationModal.utteranceExample1', defaultMessage: '"Book me a flight to Rio next week"' },
    entityCreationImageTitle: { id: 'clu.cluEntityCreationModal.entityCreationImageTitle', defaultMessage: 'Entity creation image' },
    regexDescription: {
        id: 'clu.cluEntityCreationModal.regexDescription',
        defaultMessage: 'The regex component allows you to add a regular expression to match consistent patterns.'
    },
    modalDescription: {
        id: 'clu.cluEntityCreationModal.modalDescription',
        defaultMessage: 'An entity is an item or element that is relevant to the user’s intent or goal.'
    },
    duplicatedNameErrorMessage: {
        id: 'clu.cluEntityCreationModal.duplicatedNameErrorMessage',
        defaultMessage: 'This name already exists.'
    },
    learnedDescription: {
        id: 'clu.cluEntityCreationModal.learnedDescription',
        defaultMessage:
            'The learned component uses the entity labels you label your utterances with to train a machine learned model. The model learns to predict where the entity is based on the context within the utterance.'
    },
    prebuiltDescription: {
        id: 'clu.cluEntityCreationModal.prebuiltDescription',
        defaultMessage:
            'The prebuilt component allows you to select from a library of ready-built common types such as numbers, datetimes, names and others. When added, a prebuilt component is automatically detected. '
    },
    listDescription: {
        id: 'clu.cluEntityCreationModal.listDescription',
        defaultMessage:
            'The list component represents a fixed, closed set of related words along with their synonyms. The component performs an exact text match against the list of values you provide. '
    },
    entityCompositionDescription: {
        id: 'clu.cluEntityCreationModal.entityCompositionDescription',
        defaultMessage:
            'An entity can be extracted by different methods. They can be learned through context, matched from a list, or detected by a prebuilt. Every entity in your project is composed by one or more of these methods that are defined as your entity’s components.'
    },
    examplesFooter: {
        id: 'clu.cluEntityCreationModal.examplesFooter',
        defaultMessage: '"Rio" would be under "Destination" entity, and "24th" would be "Date" entity. '
    },
    entityCreationLearnedImageTitle: {
        id: 'clu.cluEntityCreationModal.entityCreationLearnedImageTitle',
        defaultMessage: 'Entity creation learned image'
    },
    entityCreationListImageTitle: {
        id: 'clu.cluEntityCreationModal.entityCreationListImageTitle',
        defaultMessage: 'Entity creation list image'
    },
    entityCreationPrebuiltImageTitle: {
        id: 'clu.cluEntityCreationModal.entityCreationPrebuiltImageTitle',
        defaultMessage: 'Entity creation prebuilt image'
    },
    entityCreationRegexImageTitle: {
        id: 'clu.cluEntityCreationModal.entityCreationRegexImageTitle',
        defaultMessage: 'Entity creation regex image'
    },
    invalidEntityName: {
        id: 'clu.cluEntityCreationModal.invalidEntityName',
        defaultMessage: 'Invalid characters: You can only use letters (a-z, A-Z), numbers (0-9) and symbols @ _ . , ^ \\\\ [ ]'
    }
});

const { styleUlist } = getStylist('CluEntityCreationModal');
const ExampleList = styleUlist('ExampleList', () => ({ marginLeft: 50 }));

export const CluEntityCreationModal = withModalMounter(
    observer((props: CreateCluEntityModalProps) => {
        const { onSubmit, createLocally } = props;
        const { localize } = useLocalization();
        const { serviceName } = getPortalFromUrl();
        const { rootStore, dispatcher } = useGlobalState();
        const { isLargeOrGreater } = useResponsiveMode();

        const entities = rootStore.currentCluProjectStore.entityStoreHouse.items;
        const currentProject = rootStore.currentCluProjectStore;

        const { formState, formStateDto, updateFormState } = useFormState<CreateCluEntityDto>({
            name: [
                '',
                FormValidators.createRequiredValidator(),
                FormValidators.createLengthValidator(LuisConstants.Clu.maxNamesCharLength),
                FormValidators.createBlacklistValidator({
                    errorMessage: messages.invalidEntityName,
                    characterList: LuisConstants.ResourceLimits.IntentsAndEntitiesForbiddenChars
                })
            ]
        });

        const utteranceExamples = [localize(messages.utteranceExample1), localize(messages.utteranceExample2)];

        const duplicateErrorMessages =
            entities?.find(n => n.name === formState.fields.name.value) && localize(messages.duplicatedNameErrorMessage);

        const tracker = useProgressTracker({ onComplete: () => onSubmit(formStateDto) });

        const handleCreateEntity = () => {
            if (!formState.form.valid) {
                return;
            }

            if (createLocally) {
                onSubmit(formStateDto);
            } else {
                dispatcher.dispatch('addCluEntity', {
                    tracker,
                    name: formStateDto.name,
                    toastData: { failed: { title: localize(messages.addEntityFailed, { entityName: formStateDto.name }) } }
                });
            }
        };
        const handlePivotClick = (type: string) => {
            logButtonClick(serviceName, 'Schema', `Add${type}`, type, currentProject.id);
        };

        return (
            <BasicModal
                {...props}
                minWidth={750}
                disabled={tracker.isRunning}
                onSubmit={handleCreateEntity}
                doneButtonText={localize(messages.addEntity)}
                valid={formState.form.valid && !duplicateErrorMessages}
                dialogContentProps={{ title: localize(messages.modalTitle) }}>
                <Stack tokens={{ childrenGap: 4, padding: '12px 20px' }}>
                    <Stack tokens={{ childrenGap: 20 }}>
                        <LuisText>{localize(messages.modalDescription)}</LuisText>

                        <Stack verticalFill tokens={{ childrenGap: 10 }}>
                            <LuisText>{localize(messages.examplesHeader)}</LuisText>
                            <ExampleList>
                                {utteranceExamples.map((example, index) => (
                                    <li key={index}>{example}</li>
                                ))}
                            </ExampleList>
                            <LuisText>{localize(messages.examplesFooter)}</LuisText>
                        </Stack>

                        <Stack horizontal={isLargeOrGreater} tokens={{ childrenGap: 60 }}>
                            <Stack styles={{ root: { flexWrap: 'wrap', flex: 2 } }} tokens={{ childrenGap: 20 }}>
                                <LuisText fontWeight={500}>{localize(messages.nameSectionLabel)}</LuisText>

                                <LuisTextField
                                    required
                                    autoFocus
                                    data-automation-id="entityName"
                                    value={formState.fields.name.value}
                                    label={localize(messages.nameLabel)}
                                    placeholder={localize(messages.namePlaceholder)}
                                    errorMessage={formState.fields.name.errorMessage || duplicateErrorMessages}
                                    onChange={(_, name) => updateFormState({ name })}
                                    onEnterKeyUp={() => formState.form.valid && handleCreateEntity()}
                                />

                                <LuisText>{localize(messages.entityCompositionDescription)}</LuisText>
                            </Stack>

                            <Image
                                styles={{ root: { flex: 1 }, image: { width: '100%' } }}
                                alt={localize(messages.entityCreationImageTitle)}
                                src={getAssetsUrl('assets/images/clu/entityCreation.svg')}
                            />
                        </Stack>
                    </Stack>

                    <Pivot onLinkClick={link => handlePivotClick(link.props.itemKey)}>
                        <PivotItem itemKey={'Learned'} headerText={localize(messages.learned)}>
                            <Stack tokens={{ childrenGap: 12, padding: '6px 0px 0px 0px' }}>
                                <LuisText>{localize(messages.learnedDescription)}</LuisText>
                                <Image
                                    styles={{ image: { width: '100%' } }}
                                    alt={localize(messages.entityCreationLearnedImageTitle)}
                                    src={getAssetsUrl('assets/images/clu/entityCreationLearned.svg')}
                                />
                            </Stack>
                        </PivotItem>
                        <PivotItem itemKey={'List'} headerText={localize(messages.list)}>
                            <Stack tokens={{ childrenGap: 12, padding: '6px 0px 0px 0px' }}>
                                <LuisText>{localize(messages.listDescription)}</LuisText>
                                <Image
                                    styles={{ image: { width: '100%' } }}
                                    alt={localize(messages.entityCreationListImageTitle)}
                                    src={getAssetsUrl('assets/images/clu/entityCreationList.svg')}
                                />
                            </Stack>
                        </PivotItem>
                        <PivotItem itemKey={'Prebuilt'} headerText={localize(messages.prebuilt)}>
                            <Stack tokens={{ childrenGap: 12, padding: '6px 0px 0px 0px' }}>
                                <LuisText>{localize(messages.prebuiltDescription)}</LuisText>
                                <Image
                                    styles={{ image: { width: '100%' } }}
                                    alt={localize(messages.entityCreationPrebuiltImageTitle)}
                                    src={getAssetsUrl('assets/images/clu/entityCreationPrebuilt.svg')}
                                />
                            </Stack>
                        </PivotItem>
                        <PivotItem itemKey="Regex" headerText={localize(messages.regex)}>
                            <Stack tokens={{ childrenGap: 12, padding: '6px 0px 0px 0px' }}>
                                <LuisText>{localize(messages.regexDescription)}</LuisText>
                                <Image
                                    styles={{ image: { width: '100%' } }}
                                    alt={localize(messages.entityCreationRegexImageTitle)}
                                    src={getAssetsUrl('assets/images/clu/entityCreationRegex.svg')}
                                />
                            </Stack>
                        </PivotItem>
                    </Pivot>
                </Stack>
            </BasicModal>
        );
    })
);
